import Decimal from 'decimal.js';
import type { DeliveryMethod, DeliveryStatus } from './delivery';

/**
 * P11 B4 – Bảng điều phối theo ngày / xe và so phí vận chuyển đã báo giá với chi phí thực tế (docs/phases/P11.md §8).
 * Hàm thuần dùng chung server (báo cáo) + giao diện (xem trước). Mặc định Claude chọn 10/10/2026, anh Tiến đổi được.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

/** Bảng điều phối xem tối đa 31 ngày; so phí tối đa 92 ngày. */
export const DISPATCH_BOARD_MAX_DAYS = 31;
export const SHIPPING_COST_MAX_DAYS = 92;

/** Ngưỡng coi là lệch: max(50.000 đ; 10% phí báo giá). Trong ngưỡng = "Khớp". */
export const SHIPPING_COST_TOLERANCE_MIN = 50_000;
export const SHIPPING_COST_TOLERANCE_RATE = 0.1;

export interface VehicleDayLoad {
  totalKg: number;
  /** Số chuyến tối thiểu theo tải trọng xe (null = xe không khai báo tải trọng) */
  trips: number | null;
  /** Tổng kg trong ngày vượt tải trọng 1 chuyến → phải chạy nhiều chuyến */
  overload: boolean;
}

/** Tải của 1 xe trong 1 ngày: tổng kg các phiếu (không tính phiếu hủy) so với tải trọng 1 chuyến. */
export function vehicleDayLoad(maxLoadKg: number | null | undefined, weightsKg: number[]): VehicleDayLoad {
  const totalKg = weightsKg.reduce((s, w) => s.plus(w || 0), new D(0)).toDecimalPlaces(3).toNumber();
  if (!maxLoadKg || maxLoadKg <= 0) return { totalKg, trips: null, overload: false };
  const trips = Math.max(1, Math.ceil(totalKg / maxLoadKg));
  return { totalKg, trips, overload: totalKg > maxLoadKg };
}

/**
 * Km GPS của xe trong ngày chia đều cho các phiếu đã giao của xe trong ngày đó (mỗi phiếu = 1 điểm giao).
 * Làm tròn 1 số lẻ, phiếu cuối nhận phần dư để tổng khớp km GPS.
 */
export function splitDayKm(gpsKm: number, noteIds: number[]): Map<number, number> {
  const out = new Map<number, number>();
  if (!noteIds.length) return out;
  const each = new D(gpsKm).div(noteIds.length).toDecimalPlaces(1);
  let used = new D(0);
  noteIds.forEach((id, i) => {
    const v = i === noteIds.length - 1 ? new D(gpsKm).minus(used).toDecimalPlaces(1) : each;
    used = used.plus(v);
    out.set(id, v.toNumber());
  });
  return out;
}

/**
 * Phí vận chuyển báo giá của 1 phiếu: phí của đơn (dòng dịch vụ vận chuyển, chưa VAT) chia theo kg phiếu / kg cả đơn.
 * Đơn không có kg → chia đều theo số phiếu chưa hủy của đơn. Làm tròn đồng.
 */
export function quotedFeeShare(orderFee: number, noteKg: number, orderKg: number, noteCount: number): number {
  if (!orderFee) return 0;
  if (orderKg > 0) return new D(orderFee).times(Math.min(noteKg, orderKg)).div(orderKg).toDecimalPlaces(0).toNumber();
  return new D(orderFee).div(Math.max(1, noteCount)).toDecimalPlaces(0).toNumber();
}

/** Chi phí km của phiếu xe công ty = km × đ/km (làm tròn đồng); thiếu một trong hai → null. */
export function kmCost(km: number | null, costPerKm: number | null): number | null {
  if (km === null || costPerKm === null) return null;
  return new D(km).times(costPerKm).toDecimalPlaces(0).toNumber();
}

export const SHIPPING_COST_STATUSES = ['over', 'ok', 'under', 'no_quote', 'no_cost'] as const;
export type ShippingCostStatus = (typeof SHIPPING_COST_STATUSES)[number];

export const SHIPPING_COST_STATUS_LABELS: Record<ShippingCostStatus, string> = {
  over: 'Chi phí vượt phí báo',
  ok: 'Khớp',
  under: 'Báo cao hơn chi phí',
  no_quote: 'Không báo phí',
  no_cost: 'Chưa có chi phí',
};

/**
 * So phí báo giá (đã chia cho phiếu) với chi phí thực tế.
 * - Chi phí chưa xác định (thiếu km / đ/km, xe thuê chưa nhập cước) → no_cost.
 * - Không báo phí mà có chi phí > 0 → no_quote (giao miễn phí hoặc quên dòng vận chuyển).
 * - Lệch quá max(50.000 đ; 10% phí báo) → over / under; trong ngưỡng → ok.
 */
export function shippingCostStatus(quoted: number, actual: number | null): ShippingCostStatus {
  if (actual === null) return 'no_cost';
  if (!quoted) return actual > 0 ? 'no_quote' : 'ok';
  const tol = Math.max(SHIPPING_COST_TOLERANCE_MIN, quoted * SHIPPING_COST_TOLERANCE_RATE);
  if (actual - quoted > tol) return 'over';
  if (quoted - actual > tol) return 'under';
  return 'ok';
}

/** Số ngày giữa 2 ngày ISO (tính cả 2 đầu). */
export function daySpan(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;
}

/** Cột thả trên bảng điều phối: xe công ty (có id + tải trọng), Xe thuê ngoài, Khách tự lấy. */
export interface DispatchDropTarget {
  method: DeliveryMethod;
  vehicleId: number | null;
  plateNo?: string | null;
  maxLoadKg: number | null;
  /** Tổng kg phiếu đang có trên cột (trước khi thả) */
  currentKg: number;
}

export type DispatchDropPlan =
  | { kind: 'noop' }
  | { kind: 'reject'; reason: string }
  /** Xe thuê ngoài: mở hộp Xếp xe để nhập biển số */
  | { kind: 'dialog' }
  /** Gán ngay; overload ≠ null thì hỏi xác nhận trước (vượt tải 1 chuyến) */
  | { kind: 'apply'; overload: { totalKg: number; maxLoadKg: number; trips: number } | null };

/**
 * Kéo thả phiếu giao sang cột khác trên Bảng điều phối (anh Tiến 21/10/2026 "cho phép kéo thả để chọn xe").
 * Chỉ kiểm phía giao diện để báo sớm; server vẫn kiểm lại đủ luật (quyền, ngày khóa DEC-383, đủ xác nhận kho + NV kinh doanh, xe ngừng dùng).
 */
export function planDispatchDrop(
  note: { status: DeliveryStatus; method: DeliveryMethod; vehicleId: number | null; tripId: number | null; tripCode?: string | null; totalWeightKg: number },
  target: DispatchDropTarget,
  opts: { canAssignVehicle: boolean },
): DispatchDropPlan {
  const same = note.method === target.method && (target.method !== 'company_vehicle' || note.vehicleId === target.vehicleId);
  if (same) return { kind: 'noop' };
  if (note.status !== 'draft') return { kind: 'reject', reason: 'Chỉ phiếu Chờ giao mới đổi xe được' };
  if (note.tripId) return { kind: 'reject', reason: `Phiếu đang thuộc chuyến${note.tripCode ? ` ${note.tripCode}` : ''}: đổi xe ở tab Chuyến hoặc bỏ phiếu khỏi chuyến trước` };
  if (target.method === 'hired_vehicle') return { kind: 'dialog' };
  if (target.method === 'customer_pickup') return { kind: 'apply', overload: null };
  if (!target.vehicleId) return { kind: 'reject', reason: 'Chọn xe giao hàng' };
  if (!opts.canAssignVehicle) return { kind: 'reject', reason: 'Chỉ Admin hoặc Điều phối được xếp / đổi xe' };
  const load = vehicleDayLoad(target.maxLoadKg, [target.currentKg, note.totalWeightKg]);
  if (!load.overload || !target.maxLoadKg) return { kind: 'apply', overload: null };
  return { kind: 'apply', overload: { totalKg: load.totalKg, maxLoadKg: target.maxLoadKg, trips: load.trips ?? 1 } };
}

/**
 * Anh Tiến 21/10/2026 "Làm rõ theo ngày - xe": số phiếu giao mang ngày giao lúc lập (001-GH-241026 = 24/10/2026).
 * Phiếu đã đổi ngày giao → trả ngày trên số phiếu để bảng điều phối ghi "Dời từ 24/10"; cùng ngày / số lạ → null.
 */
export function movedFromDate(code: string, deliveryDate: string): string | null {
  const m = /-(\d{2})(\d{2})(\d{2})$/.exec(code.trim());
  if (!m) return null;
  const [, dd, mm, yy] = m;
  if (Number(mm) < 1 || Number(mm) > 12 || Number(dd) < 1 || Number(dd) > 31) return null;
  const iso = `20${yy}-${mm}-${dd}`;
  return iso === deliveryDate ? null : iso;
}
